import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useOptionalAuth } from '../hooks/useAuth';
import './LandingPage.css';

type Section = 'start' | 'cli' | 'domains' | 'troubleshooting' | 'api';

export default function DocsPage() {
    const [activeSection, setActiveSection] = useState<Section>('start');
    const auth = useOptionalAuth();
    const user = auth?.user;

    return (
        <div className="landing">
            <header className="landing-header">
                <div className="container">
                    <Link to="/" className="logo">🏗️ Wranglr Docs</Link>
                    <nav className="nav">
                        {user ? (
                            <>
                                <Link to="/dashboard" className="nav-link">Dashboard</Link>
                                <span className="nav-link" style={{ color: 'var(--text-secondary)' }}>{user.email?.split('@')[0]}</span>
                            </>
                        ) : (
                            <>
                                <Link to="/login" className="nav-link">Login</Link>
                                <Link to="/register" className="btn btn-primary">Get Started</Link>
                            </>
                        )}
                    </nav>
                </div>
            </header>

            <div className="docs-layout">
                {/* Sidebar */}
                <aside className="docs-sidebar">
                    <h3 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>Documentation</h3>
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                        <li style={{ marginBottom: '0.5rem' }}>
                            <button
                                onClick={() => setActiveSection('start')}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: activeSection === 'start' ? 'var(--accent-primary)' : 'var(--text-secondary)',
                                    fontWeight: activeSection === 'start' ? '600' : '400',
                                    textAlign: 'left',
                                    padding: '0.5rem 0',
                                    width: '100%'
                                }}
                            >
                                🚀 Getting Started
                            </button>
                        </li>
                        <li style={{ marginBottom: '0.5rem' }}>
                            <button
                                onClick={() => setActiveSection('cli')}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: activeSection === 'cli' ? 'var(--accent-primary)' : 'var(--text-secondary)',
                                    fontWeight: activeSection === 'cli' ? '600' : '400',
                                    textAlign: 'left',
                                    padding: '0.5rem 0',
                                    width: '100%'
                                }}
                            >
                                ⚡ CLI Tool
                            </button>
                        </li>
                        <li style={{ marginBottom: '0.5rem' }}>
                            <button
                                onClick={() => setActiveSection('domains')}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: activeSection === 'domains' ? 'var(--accent-primary)' : 'var(--text-secondary)',
                                    fontWeight: activeSection === 'domains' ? '600' : '400',
                                    textAlign: 'left',
                                    padding: '0.5rem 0',
                                    width: '100%'
                                }}
                            >
                                🌐 Custom Domains
                            </button>
                        </li>
                        <li style={{ marginBottom: '0.5rem' }}>
                            <button
                                onClick={() => setActiveSection('troubleshooting')}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: activeSection === 'troubleshooting' ? 'var(--accent-primary)' : 'var(--text-secondary)',
                                    fontWeight: activeSection === 'troubleshooting' ? '600' : '400',
                                    textAlign: 'left',
                                    padding: '0.5rem 0',
                                    width: '100%'
                                }}
                            >
                                🔧 Troubleshooting
                            </button>
                        </li>
                        <li style={{ marginBottom: '0.5rem' }}>
                            <button
                                onClick={() => setActiveSection('api')}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: activeSection === 'api' ? 'var(--accent-primary)' : 'var(--text-secondary)',
                                    fontWeight: activeSection === 'api' ? '600' : '400',
                                    textAlign: 'left',
                                    padding: '0.5rem 0',
                                    width: '100%'
                                }}
                            >
                                🔌 API Reference
                            </button>
                        </li>
                    </ul>
                </aside>

                {/* Content */}
                <main className="docs-content">
                    {activeSection === 'start' && (
                        <article>
                            <h1 style={{ marginBottom: '1.5rem' }}>Getting Started with Wranglr</h1>
                            <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                                Wranglr is the fastest way to host your static websites. Whether you have a simple HTML page or a complex React/Vue app, we can host it instantly.
                            </p>

                            <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>1. Create an Account</h3>
                            <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
                                Sign up for a free account. You don't need a credit card to get started with the Hobby plan.
                            </p>

                            <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>2. Create a Site</h3>
                            <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
                                Click "New Site" in your dashboard. Give your site a domain name (e.g., "www.example.co.za").
                            </p>

                            <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>3. Upload Files</h3>
                            <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
                                Drag and drop your project folder (e.g., your <code>dist</code> or <code>out</code> folder) into the upload area.
                                <br />
                                <strong>Important:</strong> Make sure your folder has an <code>index.html</code> file!
                            </p>

                            <div style={{ background: 'var(--bg-secondary)', padding: '1rem', borderRadius: '8px', marginTop: '1rem', border: '1px solid var(--border-color)' }}>
                                <strong>💡 Tip:</strong> <span style={{ color: 'var(--text-primary)' }}>You can also deploy using the Wranglr CLI: <code>npm install -g @wranglr/cli</code></span>
                            </div>
                        </article>
                    )}

                    {activeSection === 'cli' && (
                        <article>
                            <h1 style={{ marginBottom: '1.5rem' }}>⚡ Wranglr CLI</h1>
                            <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                                Deploy your sites from the terminal in seconds. The Wranglr CLI handles recursive directory uploads, authentication, and more.
                            </p>

                            <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Installation</h3>
                            <div style={{ background: '#1f2937', color: '#fff', padding: '1rem', borderRadius: '8px' }}>
                                <code>npm install -g @wranglr/cli</code>
                            </div>

                            <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Authentication</h3>
                            <p style={{ marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>
                                Generate an API token from <Link to="/dashboard/settings" style={{ color: 'var(--accent-primary)' }}>Settings → API Tokens</Link>, then log in:
                            </p>
                            <div style={{ background: '#1f2937', color: '#fff', padding: '1rem', borderRadius: '8px' }}>
                                <code>wranglr login --token YOUR_API_TOKEN</code>
                            </div>

                            <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Deploy a Site</h3>
                            <p style={{ marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>
                                Point the CLI at your build folder and specify the site domain:
                            </p>
                            <div style={{ background: '#1f2937', color: '#fff', padding: '1rem', borderRadius: '8px' }}>
                                <code>wranglr deploy ./dist --site www.example.co.za</code>
                            </div>
                            <p style={{ marginTop: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                                This recursively uploads all files in the <code>dist</code> directory to your site.
                            </p>

                            <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Other Commands</h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                <div style={{ background: '#1f2937', color: '#fff', padding: '1rem', borderRadius: '8px' }}>
                                    <code style={{ color: '#9ca3af' }}># List all your sites</code><br />
                                    <code>wranglr list</code>
                                </div>
                                <div style={{ background: '#1f2937', color: '#fff', padding: '1rem', borderRadius: '8px' }}>
                                    <code style={{ color: '#9ca3af' }}># Check who you're logged in as</code><br />
                                    <code>wranglr whoami</code>
                                </div>
                            </div>

                            <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>CI/CD Integration</h3>
                            <p style={{ marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>
                                Use the CLI in your CI/CD pipelines. Set the <code>WRANGLR_TOKEN</code> environment variable:
                            </p>
                            <div style={{ background: '#1f2937', color: '#fff', padding: '1rem', borderRadius: '8px' }}>
                                <code style={{ color: '#9ca3af' }}># GitHub Actions example</code><br />
                                <code>export WRANGLR_TOKEN=${'{{secrets.WRANGLR_TOKEN}}'}</code><br />
                                <code>npx @wranglr/cli deploy ./dist --site www.example.co.za</code>
                            </div>

                            <div style={{ background: 'var(--bg-secondary)', padding: '1rem', borderRadius: '8px', marginTop: '2rem', border: '1px solid var(--border-color)' }}>
                                <strong>📦 npm:</strong> <a href="https://www.npmjs.com/package/@wranglr/cli" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-primary)' }}>@wranglr/cli on npm</a>
                            </div>
                        </article>
                    )}

                    {activeSection === 'domains' && (
                        <article>
                            <h1 style={{ marginBottom: '1.5rem' }}>Connecting Custom Domains</h1>
                            <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                                You can connect any domain you own (e.g., <code>yourname.com</code> or <code>shop.co.za</code>) to your Wranglr site.
                            </p>

                            <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Step 1: Add Domain in Dashboard</h3>
                            <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
                                Go to your site settings and enter your domain name (e.g., `www.example.com`).
                            </p>

                            <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Step 2: Configure DNS</h3>
                            <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
                                Log in to where you bought your domain (like GoDaddy or Namecheap) and add a <strong>CNAME Record</strong>:
                            </p>

                            <div style={{ background: '#1f2937', color: '#fff', padding: '1rem', borderRadius: '8px', overflowX: 'auto' }}>
                                <code style={{ fontFamily: 'monospace' }}>
                                    Type: CNAME<br />
                                    Name: www<br />
                                    Target: proxy.wranglr.co.za
                                </code>
                            </div>

                            <p style={{ marginTop: '1rem', color: 'var(--text-secondary)' }}>
                                If you are using a root domain (like `example.com` without www), most providers don't support CNAMEs on root. We recommend using `www` for the best reliability.
                            </p>

                            <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Step 3: Wait for Verification</h3>
                            <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
                                It can take anywhere from 5 minutes to 24 hours for DNS to propagate. Once verified, the SSL lock 🔒 will appear automatically.
                            </p>
                        </article>
                    )}

                    {activeSection === 'troubleshooting' && (
                        <article>
                            <h1 style={{ marginBottom: '1.5rem' }}>Troubleshooting</h1>

                            <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Site Returns 404 Not Found</h3>
                            <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
                                - Did you upload an `index.html` file?<br />
                                - Is your subscription active?<br />
                                - If using a custom domain, have you configured the DNS records correctly?
                            </p>

                            <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>"Subscription Expired" Page</h3>
                            <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
                                This means your trial has ended or your payment failed. Go to the dashboard and check your billing status to renew access immediately.
                            </p>

                            <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>SSL Certificate Pending</h3>
                            <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
                                If your site says "Not Secure" or the certificate is pending for more than 24 hours, double-check your DNS CNAME record. It must point <strong>exactly</strong> to `proxy.wranglr.co.za`.
                            </p>
                        </article>
                    )}

                    {activeSection === 'api' && (
                        <article>
                            <h1 style={{ marginBottom: '1.5rem' }}>API Reference</h1>
                            <p style={{ marginBottom: '1rem', color: '#4b5563' }}>
                                Automate your deployments with the Wranglr API. All requests must include the <code>Authorization: Bearer &lt;token&gt;</code> header.
                            </p>

                            <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Get Sites</h3>
                            <div className="code-block" style={{ background: '#1f2937', color: '#fff', padding: '1rem', borderRadius: '8px' }}>
                                <code>GET https://api.wranglr.co.za/api/sites</code>
                            </div>

                            <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Create Site</h3>
                            <div className="code-block" style={{ background: '#1f2937', color: '#fff', padding: '1rem', borderRadius: '8px' }}>
                                <code>POST https://api.wranglr.co.za/api/sites</code>
                                <pre style={{ marginTop: '0.5rem', color: '#9ca3af', fontSize: '0.9rem' }}>
                                    {JSON.stringify({ domain: "my-app" }, null, 2)}
                                </pre>
                            </div>

                            <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Direct Upload</h3>
                            <p style={{ marginBottom: '0.5rem', color: '#4b5563' }}>Upload a file directly to your site.</p>
                            <div className="code-block" style={{ background: '#1f2937', color: '#fff', padding: '1rem', borderRadius: '8px' }}>
                                <code>POST https://api.wranglr.co.za/api/upload/direct/:siteId/:path</code>
                            </div>
                        </article>
                    )}
                </main>
            </div>

            <footer className="landing-footer">
                <div className="container">
                    <p>© 2024 Wrangler.co.za. Made with ❤️ in South Africa.</p>
                </div>
            </footer>
        </div>
    );
}
